import {
  getDataDetail,
  publishDemand, // 做数据集投诉用
  applyQualityReport,
  ApplyQualityReportParams,
  setDataView,
} from "@/api/dataset";
import { getApiDetail } from "@/api/apiDataset";
import type { ApiDetailResponse } from "@/api/apiDataset/types";
import { getAssessmentAlgorithm } from "@/api/algorithm";
import type { DataDetail } from "@/api/dataset";
import { Commit, Module } from "vuex";
import { formatBytes, milliFormat, formatNum } from "@/utils/format";
import { t } from "@/plugins/i18n";
import { RootState } from "@/store";
import dayjs from "dayjs";

type InfoItem = {
  key: string;
  label: string;
  value: any;
};

interface State {
  dataDetail: DataDetail;
  dataDetailInfo: {
    infoList: InfoItem[];
    datasetName: string;
  };
  supportAlgorithm: string[];
  infoLoading: boolean;
  apiDataDetail: ApiDetailResponse;
  apiDataDetailInfo: {
    infoList: InfoItem[];
    datasetName: string;
  };
  apiInfoLoading: boolean;
}

const infoConfig = {
  createCompanyName: t("dataset.dataSaler"),
  pattern: t("dataset.suffix"),
  datasetCode: t("dataset.details.datasetHash"),
  createTime: t("dataset.uploadTime"),
  salesVolume: t("dataset.salesVolume"),
  datasetSize: t("dataset.fileSize"),
  tag: t("dataset.dataTag"),
  description: t("dataset.salerDesc"),
};
const apiInfoConfig = {
  creator: t("dataset.apiDataSaler"),
  apiCode: t("dataset.details.apiDatasetHash"),
  createTime: t("dataset.uploadTime"),
  salesVolume: t("dataset.salesVolume"),
  tag: t("dataset.apiDataTag"),
  description: t("dataset.salerDesc"),
};
const dataTypeIconConfig = {
  "3": "icon-biaoge",
  "1": "icon-wenben",
  "4": "icon-tuxiang",
  "5": "icon-yasuobao",
  "2": "icon-shipin",
  "6": "icon-qita",
  "0": "icon-API",
};

const processValue = (key: string, value: any) => {
  switch (key) {
    case "createTime":
      return dayjs(value).format("YYYY/MM/DD");
    case "price":
      return milliFormat(formatNum(value));
    case "datasetSize":
      return formatBytes(value);
    case "tag":
      return value && value.split(";");
    default:
      return value;
  }
};

const store: Module<State, RootState> = {
  namespaced: true, // 为每个模块添加一个前缀名，保证模块命明不冲突
  state: () => {
    return {
      dataDetail: {
        id: 0,
        datasetName: "",
        price: 0,
        originalPrice: 0,
        tag: "",
        datasetSize: 0,
        pattern: "",
        hash: "",
        patternFormat: "",
        description: "",
        createCompanyName: "",
        createTime: 0,
        salesVolume: 0,
        datasetCode: "",
        isBuy: 0,
        auditType: 0,
        assessmentMethodDTOList: [],
        firmName: "",
        bankName: "",
        bankAccount: "",
      },
      infoLoading: true,
      dataDetailInfo: {
        infoList: [],
        datasetName: "",
        price: null,
        originalPrice: null,
        dataType: "",
      },
      supportAlgorithm: [],
      apiDataDetail: {
        apiId: 0,
        apiName: "",
        apiCode: "",
        tag: "",
        price: 0,
        requestHeaders: [],
        requestMethod: 0,
        queryParams: [],
        bodyParams: [],
        javaRequestExample: "",
        pythonRequestExample: "",
        exampleCodeList: [],
        successExample: "",
        failExample: "",
        successCodeExample: [],
        errorCodeExample: [],
        description: "",
        descriptionTxt: "",
        callResult: "",
        salesVolume: 0,
        creator: "",
        createTimeToLong: 0,
        createTime: "",
        mappingUrl: "",
        apiUrl: "",
        imageList: [],
      },
      apiDataDetailInfo: {
        infoList: [],
        datasetName: "",
        price: null,
        originalPrice: null,
        dataType: "",
      },
      apiInfoLoading: true,
    };
  },
  mutations: {
    UPDATE_IS_BUY(state, payload) {
      if (state.dataDetail) {
        state.dataDetail.isBuy = payload;
      }
    },
    setDataDetail(state, payload) {
      state.dataDetail = payload;
    },
    setDetailInfo(state, payload) {
      state.dataDetailInfo = payload;
    },
    setSupportAlgorithm(state, payload) {
      state.supportAlgorithm = payload;
    },
    setDetailLoading(state, payload) {
      state.infoLoading = payload;
    },
    setAPIDataDetail(state, payload) {
      state.apiDataDetail = payload;
    },
    setAPIDetailInfo(state, payload) {
      state.apiDataDetailInfo = payload;
    },
    setAPIDetailLoading(state, payload) {
      state.apiInfoLoading = payload;
    },
  },
  actions: {
    async getAlgorithmByPattern({ commit }: { commit: Commit }, payload) {
      try {
        const res = await getAssessmentAlgorithm(payload);
        log.debug("getAlgorithmByPattern res", res);
        commit("setSupportAlgorithm", res || []);
      } catch (error) {
        log.error(error, "error");
      }
    },
    async getDataDetail(
      { commit, rootState }: { commit: Commit; rootState: RootState },
      params
    ) {
      try {
        commit("setDetailLoading", true);
        commit("setDataDetail", null);
        commit("setDetailInfo", null);
        const res = await getDataDetail(params);
        if (res === null) {
          commit("setDataDetail", res);
          commit("setDetailInfo", res);
        } else {
          const infoList: InfoItem[] = [];
          Object.keys(infoConfig).forEach((key) => {
            infoList.push({
              key,
              label: infoConfig[key as keyof typeof infoConfig],
              value: processValue(key, res[key as keyof typeof infoConfig]),
            });
          });
          commit("setDetailInfo", {
            infoList,
            price: res.price,
            originalPrice: res.originalPrice,
            datasetName: res.datasetName,
            dataType:
              res.patternFormat === "1"
                ? res.pattern === "pdf"
                  ? "icon-PDF-2"
                  : "icon-wenben"
                : (dataTypeIconConfig[
                    res.patternFormat as keyof typeof dataTypeIconConfig
                  ] as string),
          });
          commit("setDataDetail", res);
          setDataView({
            datasetId: params.datasetId,
            datasetName: res.datasetName,
            userId: rootState.user?.userInfo?.id ?? null,
            type: 0,
          });
          if (window._paq) {
            window._paq.push([
              "setEcommerceView",
              res.id.toString(), // (Required) productSKU
              res.datasetName, // (Optional) productName
              false, // (Optional) categoryName
              res.price, // (Optional) price
            ]);
            window._paq.push(["trackPageView"]);
          }
        }
        commit("setDetailLoading", false);
      } catch (error) {
        commit("setDetailLoading", false);
        log.error(error, "error");
      }
      return;
    },
    async getAPIDataDetail(
      { commit, rootState }: { commit: Commit; rootState: RootState },
      params
    ) {
      try {
        commit("setAPIDetailLoading", true);
        commit("setAPIDataDetail", null);
        commit("setAPIDetailInfo", null);
        const res = await getApiDetail(params);
        if (res === null) {
          commit("setAPIDataDetail", res);
          commit("setAPIDetailInfo", res);
        } else {
          const infoList: InfoItem[] = [];
          Object.keys(apiInfoConfig).forEach((key) => {
            infoList.push({
              key,
              label: apiInfoConfig[key as keyof typeof apiInfoConfig],
              value: processValue(key, res[key as keyof typeof apiInfoConfig]),
            });
          });
          commit("setAPIDetailInfo", {
            infoList,
            price: res.price,
            originalPrice: res.price,
            datasetName: res.apiName,
            dataType: "icon-API",
          });
          commit("setAPIDataDetail", res);
          setDataView({
            datasetId: params.apiId,
            datasetName: res.apiName,
            userId: rootState.user?.userInfo?.id ?? null,
            type: 1,
          });
          if (window._paq) {
            window._paq.push([
              "setEcommerceView",
              res.apiId.toString(), // (Required) productSKU
              res.apiName, // (Optional) productName
              false, // (Optional) categoryName
              res.price, // (Optional) price
            ]);
            window._paq.push(["trackPageView"]);
          }
        }
        commit("setAPIDetailLoading", false);
      } catch (error) {
        commit("setAPIDetailLoading", false);
        log.error(error, "error");
      }
      return;
    },

    async applyReport(
      { commit }: { commit: Commit },
      payload: {
        params: ApplyQualityReportParams;
        onSuccess: () => void;
        onError: () => void;
      }
    ) {
      const { params, onSuccess, onError } = payload;
      try {
        const res = await applyQualityReport(params);
        if (res === "SUCCESS") {
          onSuccess();
        } else {
          throw new Error(res);
        }
      } catch (error) {
        onError();
      }
    },
    async complainData({ commit }: { commit: Commit }, payload) {
      const { successCb, params, errorCb } = payload;
      try {
        await publishDemand(params);
        successCb();
      } catch (error) {
        errorCb(error);
      }
    },
  },
};

export default store;
